// Shared SO rebar-pricing math (ticket 86d39hzbk). Single source of truth so the
// line-items table and the Order-Details Pricing panel can never disagree.
//
// All math mirrors the backend submit math (full precision, unrounded):
//   base          = item price at the chosen level (Average -> averagePricePerPc, else basePricePerPc)
//   rateWithMarkup = base × (1 + FO grade markup/100)   ← markup is a WHOLE PERCENT (86.02 = 86.02%)
//   amount        = rateWithMarkup × quantity      (ex-VAT line value)
//   grossAmount   = amount × (1 + rebar VAT rate)
//
// REBAR ONLY. Freight (its charge + Freight12% tax) is owned by the freight work,
// not this util. NetSuite computes the authoritative totals on submit — these are previews.

import type { FirmOfferDetailDto, SalesOrderLineItemDto } from '../types/salesOrder.types';
import { lineWeightKg } from './lineWeight';

// Fallback rebar VAT when the FO customer's synced rate is unavailable (null). The
// authoritative rate is firmOffer.pricing.rebarTaxRate (0 for a zero-rated customer);
// this only covers "no FO / customer not in the synced table yet".
export const DEFAULT_REBAR_VAT = 0.12;

// SO grade code (G33…G80) -> PNS grade label (230…550), the key the FO per-grade
// pricing/balance maps use.
export const GRADE_CODE_TO_PNS: Record<string, string> = {
  '33': '230',
  '40': '280',
  '60': '420',
  '75': '520',
  '80': '550',
};

// PNS label -> the FO per-grade pricing key (grade230…grade550).
const PNS_TO_PRICING_KEY: Record<string, keyof FirmOfferDetailDto['pricing']['perGrade']> = {
  '230': 'grade230',
  '280': 'grade280',
  '420': 'grade420',
  '520': 'grade520',
  '550': 'grade550',
};

// The single uniform rebar VAT rate to use for a draft: the FO customer's synced rate
// when present (incl. an explicit 0 for zero-rated), else the default fallback.
export function rebarVatRate(firmOffer: FirmOfferDetailDto | null): number {
  return firmOffer?.pricing.rebarTaxRate ?? DEFAULT_REBAR_VAT;
}

// FO per-grade markup as a WHOLE PERCENT (e.g. 86.02 = 86.02%) for a line's grade. 0 when
// no FO / no grade. computeLinePricing divides by 100 when applying it as a rate multiplier.
export function markupForGrade(gradeCode: string | null, fo: FirmOfferDetailDto | null): number {
  if (!gradeCode || !fo) return 0;
  const pns = GRADE_CODE_TO_PNS[gradeCode];
  const key = pns ? PNS_TO_PRICING_KEY[pns] : undefined;
  if (!key) return 0;
  return fo.pricing.perGrade[key]?.markupPct ?? 0;
}

// Round half-up to 2 decimals (centavo). Currency math must not lean on bare
// Math.round — a true `.xx5` that float stored as `.xx4999…` would round DOWN.
// The sign-aware epsilon nudges it back over the half-up boundary; it is many
// orders of magnitude below a centavo so it never moves a real value. 86d3fu6tc.
export function round2(value: number): number {
  const nudge = value >= 0 ? 1e-9 : -1e-9;
  return Math.round((value + nudge) * 100) / 100;
}

export interface LinePricing {
  rate: number | null;
  rateWithMarkup: number | null;
  amount: number | null;
  // VAT-inclusive per-piece unit, rounded to the centavo. This is the value the
  // customer PO is built from (`round(unit, 2)` then `× qty`), so `grossAmount`
  // derives from it — NOT from the unrounded `amount × (1 + tax)`. 86d3fu6tc.
  grossRate: number | null;
  grossAmount: number | null;
}

// Per-line pricing. `markup` is the FO per-grade markup as a WHOLE PERCENT, e.g. 86.02
// (0 when no FO line for the grade -> rate = base), divided by 100 for the multiplier.
// `taxRate` is the uniform rebar VAT (0 for zero-rated).
export function computeLinePricing(
  li: SalesOrderLineItemDto,
  markup: number,
  taxRate: number,
): LinePricing {
  const base = li.priceLevel === '2' ? li.averagePricePerPc : li.basePricePerPc;
  const rate = base ?? null;
  // Ex-VAT values stay FULL PRECISION so they keep mirroring the NetSuite ex-VAT
  // posting (the SO line rate is ex-VAT; NetSuite adds the 12% as a tax line). 86d3fu6tc.
  const rateWithMarkup = base != null ? base * (1 + markup / 100) : null;
  const amount = rateWithMarkup != null && li.quantity != null ? rateWithMarkup * li.quantity : null;
  // VAT-inclusive path rounds the per-piece unit to the centavo BEFORE × qty, to
  // match the customer PO convention (`round(unit, 2) × qty`). 86d3fu6tc.
  const grossRate = rateWithMarkup != null ? round2(rateWithMarkup * (1 + taxRate)) : null;
  const grossAmount =
    grossRate != null && li.quantity != null ? round2(grossRate * li.quantity) : null;
  return { rate, rateWithMarkup, amount, grossRate, grossAmount };
}

// Per-grade kg requested across the draft, keyed by SO grade code. Each line weighs
// pieces × kg per piece (lineWeightKg, 14ykpqtv3qw) — the same weight the submit check uses.
export function buildPerGradeRequested(
  items: SalesOrderLineItemDto[],
): Map<string, number> {
  const m = new Map<string, number>();
  for (const li of items) {
    const weight = lineWeightKg(li);
    if (!li.gradeCode || weight == null) continue;
    m.set(li.gradeCode, (m.get(li.gradeCode) ?? 0) + weight);
  }
  return m;
}

export interface GradeAggregate {
  pns: string; // PNS label (230…550)
  weightKg: number;
  qty: number;
  estValue: number; // Σ line amount (ex-VAT) for this grade
}

export interface RebarPricingSummary {
  totalWeightKg: number;
  totalQty: number;
  lineCount: number;
  taxRate: number;
  // Per-grade rollup keyed by PNS label, only grades present in the draft.
  perGrade: GradeAggregate[];
  subtotal: number; // Σ line amount (ex-VAT), rounded
  // 86d3fu6tc — option A: grandTotal is Σ per-line ROUNDED gross (ties to the
  // table's Gross Amount column); vat is the residual, not subtotal × taxRate.
  vat: number; // grandTotal − subtotal
  grandTotal: number; // Σ round(grossRate × qty)
}

// Order-level rebar rollup for the Pricing panel: totals, per-grade weight/qty/est-value,
// and est subtotal/VAT/grand-total. Uses the same per-line math as the table.
//
// 86d3cwcn9: `frozenMarkups` / `frozenTaxRate` mirror the line-items table's frozen-snapshot
// path (SalesOrderLineItemsTable). For read-only/submitted SOs the live FO re-fetch is
// skipped, so `firmOffer` is null — passing the draft's frozen markup map + VAT rate keeps
// the rollup (and the freight block's order total) on the marked-up rates instead of
// silently collapsing the markup to 0. Omit both for the editable-draft path (live FO).
// `frozenMarkups` is keyed by the raw SO grade code (33/40/60/75/80), NOT the PNS label.
export function aggregateRebarPricing(
  lineItems: SalesOrderLineItemDto[],
  firmOffer: FirmOfferDetailDto | null,
  frozenMarkups?: Record<string, number>,
  frozenTaxRate?: number,
): RebarPricingSummary {
  const taxRate = frozenTaxRate ?? rebarVatRate(firmOffer);
  // Frozen snapshot (read-only) vs live FO — both feed computeLinePricing identically; the
  // frozen value is the same markupPct captured at draft-save. Mirrors the table's markupOf.
  const markupOf = (gradeCode: string | null): number =>
    frozenMarkups
      ? gradeCode != null
        ? frozenMarkups[gradeCode] ?? 0
        : 0
      : markupForGrade(gradeCode, firmOffer);
  const byGrade = new Map<string, GradeAggregate>();
  let totalWeightKg = 0;
  let totalQty = 0;
  let subtotalRaw = 0;
  let grandTotalRaw = 0;

  for (const li of lineItems) {
    const weight = lineWeightKg(li) ?? 0;
    const qty = li.quantity ?? 0;
    const { amount, grossAmount } = computeLinePricing(li, markupOf(li.gradeCode), taxRate);
    totalWeightKg += weight;
    totalQty += qty;
    subtotalRaw += amount ?? 0;
    // 86d3fu6tc — option A: the canonical order total is the sum of the per-line
    // ROUNDED gross, so this rollup ties to the line-items table's Gross Amount
    // column to the centavo. VAT is then the residual (grandTotal − subtotal),
    // not subtotal × taxRate.
    grandTotalRaw += grossAmount ?? 0;

    const pns = li.gradeCode ? (GRADE_CODE_TO_PNS[li.gradeCode] ?? li.gradeCode) : '—';
    const existing = byGrade.get(pns);
    if (existing) {
      existing.weightKg += weight;
      existing.qty += qty;
      existing.estValue += amount ?? 0;
    } else {
      byGrade.set(pns, { pns, weightKg: weight, qty, estValue: amount ?? 0 });
    }
  }

  const subtotal = round2(subtotalRaw);
  const grandTotal = round2(grandTotalRaw);
  const vat = round2(grandTotal - subtotal);
  return {
    totalWeightKg,
    totalQty,
    lineCount: lineItems.length,
    taxRate,
    perGrade: [...byGrade.values()],
    subtotal,
    vat,
    grandTotal,
  };
}

// kg -> metric tons (1 MT = 1000 kg). "per MT" and "per Ton" are the same unit here.
export function kgToMt(kg: number): number {
  return kg / 1000;
}
